import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Rating,
  Drawer,
  Stepper,
  Step,
  StepLabel,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ChatBubbleOutline as ChatIcon,
  Send as SendIcon,
  CheckCircleOutline as ConfirmIcon,
  CancelOutlined as RejectIcon,
  ArticleOutlined as PreviewIcon,
  Launch as LaunchIcon,
  ImageOutlined as ImageIcon,
  DescriptionOutlined as DescriptionIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  LinkOutlined as LinkIcon,
  DeleteOutline as DeleteOutlineIcon,
  StarOutline as StarOutlineIcon,
} from "@mui/icons-material";
import axios from "axios";
import { io } from "socket.io-client";
import { useAuth } from "../../contexts/AuthContext";
import PendingWorks from "../Bills/PendingWorks";

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    let base = process.env.REACT_APP_API_URL;
    base = base.endsWith("/") ? base.slice(0, -1) : base;
    if (base.endsWith("/api")) {
      base = base.slice(0, -4);
    }
    return base;
  }
  if (
    typeof window !== "undefined" &&
    (window.location.hostname.includes("ondigitalocean.app") ||
      window.location.hostname.includes("services.knls.in"))
  ) {
    return ""; // For these domains, APIs are relative to current origin
  }
  return "http://localhost:3000";
};

const getSocketUrl = () => {
  // Socket.IO endpoint - use explicit backend URL for production
  if (process.env.REACT_APP_SOCKET_URL) {
    return process.env.REACT_APP_SOCKET_URL;
  }

  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    // For services.knls.in frontend, connect to the DigitalOcean backend
    if (hostname.includes("services.knls.in")) {
      return "https://locsy-lq54y.ondigitalocean.app";
    }

    // For localhost or direct access
    if (hostname.includes("localhost") || hostname.includes("127.0.0.1")) {
      return "http://localhost:3000";
    }

    // For ondigitalocean.app, use the same origin (relative path)
    if (hostname.includes("ondigitalocean.app")) {
      return `${protocol}//${hostname}`;
    }
  }

  return "";
};

const operatorRoles = [
  "app_operator",
  "app_admin",
  "super_admin",
  "net_center_admin",
  "shop_admin",
  "franchisee_admin",
];

const steps = [
  "Fill Form",
  "Send Preview",
  "User Confirmation",
  "Final Submit",
];

const ApplicationChat = () => {
  const { user, token } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const apiBase = useMemo(() => getApiBaseUrl(), []);
  const apiUrl = apiBase ? `${apiBase}/api` : "/api";
  const socketUrl = useMemo(() => getSocketUrl(), []);

  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState("");
  const [newConversationOpen, setNewConversationOpen] = useState(false);
  const [newConversationPhone, setNewConversationPhone] = useState("");
  const [newConversationAppId, setNewConversationAppId] = useState("");
  const [newConversationError, setNewConversationError] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const [autoSelectDone, setAutoSelectDone] = useState(false);
  const [messageMenuAnchor, setMessageMenuAnchor] = useState(null);
  const [messageMenuMessage, setMessageMenuMessage] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteDialogMessage, setDeleteDialogMessage] = useState(null);
  const [mobileListOpen, setMobileListOpen] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [socketDisabled, setSocketDisabled] = useState(false);
  const socketRef = useRef(null);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const chatPanelRef = useRef(null);
  const connectionErrorCount = useRef(0);
  const lastErrorTime = useRef(0);

  const isOperator = operatorRoles.includes(user?.role);
  const resolveToken = () => {
    const isJwtLike = (value) =>
      typeof value === "string" && value.split(".").length === 3;
    const candidates = [token];
    if (typeof window !== "undefined") {
      candidates.push(
        localStorage.getItem("user_token"),
        localStorage.getItem("admin_token"),
        localStorage.getItem("token"),
      );
    }

    for (const candidate of candidates) {
      if (!candidate) continue;
      if (candidate === "DEV_BYPASS" || isJwtLike(candidate)) {
        return candidate;
      }
    }
    return null;
  };

  const resolvedToken = resolveToken();
  const authHeaders = resolvedToken
    ? { Authorization: `Bearer ${resolvedToken}` }
    : {};

  const activeStep = useMemo(() => {
    if (!selectedConversation?.confirmation_status) {
      return 1;
    }
    if (selectedConversation.confirmation_status === "confirmed") {
      return 3;
    }
    if (selectedConversation.confirmation_status === "pending") {
      return 2;
    }
    if (selectedConversation.confirmation_status === "rejected") {
      return 1;
    }
    return 1;
  }, [selectedConversation]);

  useEffect(() => {
    if (!socketUrl || socketDisabled) {
      console.log("⚠️ Socket.IO disabled due to repeated CORS failures");
      return;
    }

    console.log("🔧 Socket.IO Configuration:");
    console.log("   Backend URL:", socketUrl);
    console.log("   Frontend URL:", window.location.href);
    console.log("   Frontend Origin:", window.location.origin);
    console.log("   Has Token:", !!resolvedToken);

    const socket = io(socketUrl, {
      path: "/socket.io",
      transports: ["polling", "websocket"],
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
      reconnectionAttempts: 5,
      timeout: 20000,
      autoConnect: true,
      withCredentials: true,
      upgrade: true,
      rememberUpgrade: true,
      auth: resolvedToken ? { token: resolvedToken } : undefined,
      extraHeaders: authHeaders,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      console.log(
        "✅ Socket connected:",
        socket.id,
        "Transport:",
        socket.io.engine.transport.name,
      );
      console.log("🔌 Connected to:", socketUrl);
      setSocketConnected(true);
    });

    socket.on("disconnect", (reason) => {
      console.log("❌ Socket disconnected:", reason);
      setSocketConnected(false);
    });

    socket.on("connect_error", (error) => {
      const now = Date.now();
      // Only log error once per 10 seconds to avoid spam
      if (now - lastErrorTime.current > 10000) {
        console.error("❌ Socket connection error:", error);
        console.error("Error message:", error.message);
        console.log("ℹ️ Socket.IO will automatically try polling fallback");
        lastErrorTime.current = now;
      }

      connectionErrorCount.current += 1;
      setSocketConnected(false);
    });

    socket.on("reconnect", (attemptNumber) => {
      console.log("🔄 Socket reconnected after", attemptNumber, "attempts");
      connectionErrorCount.current = 0; // Reset error count on successful reconnect
      setSocketConnected(true);
    });

    socket.on("reconnect_attempt", (attemptNumber) => {
      console.log("🔄 Socket reconnection attempt:", attemptNumber);
    });

    socket.on("reconnect_error", (error) => {
      console.error("❌ Socket reconnection error:", error.message);
    });

    socket.on("reconnect_failed", () => {
      console.error("❌ Socket reconnection failed - all attempts exhausted");
      console.log("ℹ️ Will retry connection when user sends a message");
      setSocketConnected(false);
    });

    // Log transport events
    socket.io.engine.on("upgrade", (transport) => {
      console.log("⬆️ Transport upgraded to:", transport.name);
    });

    socket.io.engine.on("upgrade_error", (error) => {
      console.log(
        "⚠️ Transport upgrade failed:",
        error,
        "- continuing with current transport",
      );
    });

    socket.on("chat-message", (payload) => {
      if (payload?.conversation_id === selectedConversation?.id) {
        setMessages((prev) => [...prev, payload]);
      }

      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === payload?.conversation_id
            ? {
                ...conv,
                last_message_content: payload.content,
                last_message_type: payload.message_type,
                last_message_sender: payload.sender_type,
                last_message_created_at: payload.created_at,
                last_message_at: payload.created_at,
              }
            : conv,
        ),
      );
    });

    socket.on("chat-message-deleted", (payload) => {
      if (payload?.conversation_id === selectedConversation?.id) {
        setMessages((prev) =>
          prev.map((message) =>
            message.id === payload?.message_id
              ? {
                  ...message,
                  deleted_for_everyone: true,
                  content: { text: "This message was deleted." },
                }
              : message,
          ),
        );
      }
    });

    socket.on("confirmation-updated", (payload) => {
      setConversations((prev) =>
        prev.map((conv) =>
          conv.application_id === payload?.application_id
            ? {
                ...conv,
                confirmation_status: payload.status,
                confirmed_at: payload.confirmed_at,
                confirmed_by: payload.confirmed_by,
              }
            : conv,
        ),
      );

      if (selectedConversation?.application_id === payload?.application_id) {
        setSelectedConversation((prev) => ({
          ...prev,
          confirmation_status: payload.status,
          confirmed_at: payload.confirmed_at,
          confirmed_by: payload.confirmed_by,
        }));
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [
    socketUrl,
    selectedConversation?.id,
    selectedConversation?.application_id,
    socketDisabled,
  ]);

  const refreshMessages = async (conversationId) => {
    if (!conversationId) return;
    try {
      const response = await axios.get(
        `${apiUrl}/confirmations/conversations/${conversationId}/messages`,
        {
          headers: authHeaders,
        },
      );
      setMessages(response.data.data || []);
      if (response.data.conversation) {
        setSelectedConversation(response.data.conversation);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load messages");
    }
  };

  useEffect(() => {
    if (!selectedConversation?.id) return undefined;
    if (socketConnected) return undefined;

    // HTTP polling fallback - only when socket is disabled/failed
    // Increased from 5s to 30s to reduce server load
    const intervalId = setInterval(() => {
      refreshMessages(selectedConversation.id);
    }, 30000); // 30 seconds instead of 5

    return () => clearInterval(intervalId);
  }, [socketConnected, selectedConversation?.id]);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (autoSelectDone || !conversations.length) return;
    const params = new URLSearchParams(location.search);
    const targetId = params.get("conversationId");
    if (!targetId) return;
    const match = conversations.find((conv) => conv.id === targetId);
    if (match) {
      handleSelectConversation(match.id).finally(() => setAutoSelectDone(true));
    } else {
      setAutoSelectDone(true);
    }
  }, [autoSelectDone, conversations, location.search]);

  useEffect(() => {
    if (selectedConversation?.id && socketRef.current) {
      socketRef.current.emit("join-conversation", selectedConversation.id);
    }
    return () => {
      if (selectedConversation?.id && socketRef.current) {
        socketRef.current.emit("leave-conversation", selectedConversation.id);
      }
    };
  }, [selectedConversation?.id]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const fetchConversations = async () => {
    setLoadingConversations(true);
    setError("");
    try {
      const response = await axios.get(
        `${apiUrl}/confirmations/conversations`,
        {
          headers: authHeaders,
        },
      );
      setConversations(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load conversations");
    } finally {
      setLoadingConversations(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    setLoadingMessages(true);
    setError("");
    try {
      const response = await axios.get(
        `${apiUrl}/confirmations/conversations/${conversationId}/messages`,
        {
          headers: authHeaders,
        },
      );
      setMessages(response.data.data || []);
      if (response.data.conversation) {
        setSelectedConversation(response.data.conversation);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load messages");
    } finally {
      setLoadingMessages(false);
    }
  };

  const formatFileSize = (bytes = 0) => {
    if (!bytes) return "";
    const units = ["B", "KB", "MB", "GB"];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex += 1;
    }
    return `${size.toFixed(size >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
  };

  const resolveMediaUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    const base =
      apiBase || (typeof window !== "undefined" ? window.location.origin : "");
    if (!base) return url;
    if (url.startsWith("/")) return `${base}${url}`;
    return `${base}/${url}`;
  };

  const scrollToChatPanel = () => {
    if (chatPanelRef.current) {
      chatPanelRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const handleSelectConversation = async (conversationId) => {
    await fetchMessages(conversationId);
    if (isMobile) {
      setMobileListOpen(false);
    }
    scrollToChatPanel();
  };

  const sendMessage = async (messageType, content) => {
    if (!selectedConversation?.id) return;
    try {
      await axios.post(
        `${apiUrl}/confirmations/conversations/${selectedConversation.id}/messages`,
        { messageType, content },
        { headers: authHeaders },
      );
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send message");
    }
  };

  const uploadChatImage = async (file) => {
    if (!selectedConversation?.id) return null;
    const formData = new FormData();
    formData.append("file", file);

    try {
      setUploadingImage(true);
      const response = await axios.post(
        `${apiUrl}/confirmations/conversations/${selectedConversation.id}/uploads`,
        formData,
        {
          headers: {
            ...authHeaders,
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return response.data?.data || null;
    } catch (err) {
      setError(err.response?.data?.message || "Failed to upload image");
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const uploadChatDocument = async (file) => {
    if (!selectedConversation?.id) return null;
    const formData = new FormData();
    formData.append("file", file);

    try {
      setUploadingDocument(true);
      const response = await axios.post(
        `${apiUrl}/confirmations/conversations/${selectedConversation.id}/uploads`,
        formData,
        {
          headers: {
            ...authHeaders,
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return response.data?.data || null;
    } catch (err) {
      setError(err.response?.data?.message || "Failed to upload document");
      return null;
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleSendImageFile = async (file) => {
    if (!file) return;
    const uploaded = await uploadChatImage(file);
    if (!uploaded?.url) return;
    await sendMessage("image", {
      url: uploaded.url,
      name: uploaded.name || file.name,
      mime: uploaded.mime || file.type,
      size: uploaded.size || file.size,
    });
  };

  const handleSendDocumentFile = async (file) => {
    if (!file) return;
    const uploaded = await uploadChatDocument(file);
    if (!uploaded?.url) return;
    await sendMessage("document", {
      url: uploaded.url,
      name: uploaded.name || file.name,
      mime: uploaded.mime || file.type,
      size: uploaded.size || file.size,
    });
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (file) {
      await handleSendImageFile(file);
    }
    event.target.value = "";
  };

  const handleDocumentChange = async (event) => {
    const file = event.target.files?.[0];
    if (file) {
      await handleSendDocumentFile(file);
    }
    event.target.value = "";
  };

  const handlePasteImage = async (event) => {
    const items = event.clipboardData?.items || [];
    const imageItem = Array.from(items).find((item) =>
      item.type.startsWith("image/"),
    );
    if (!imageItem) return;
    const file = imageItem.getAsFile();
    if (file) {
      event.preventDefault();
      await handleSendImageFile(file);
    }
  };

  const openWhatsAppShare = (url) => {
    const text = `Screenshot: ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const openTelegramShare = (url) => {
    const text = "Screenshot";
    window.open(
      `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
      "_blank",
    );
  };

  const openWhatsAppShareLink = (url) => {
    const text = `Open your chat: ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const handleShareChatLink = async () => {
    if (!selectedConversation?.id) return;
    try {
      setMagicLinkLoading(true);
      const response = await axios.post(
        `${apiUrl}/confirmations/conversations/${selectedConversation.id}/magic-link`,
        {},
        { headers: authHeaders },
      );
      const url = response.data?.data?.url;
      if (!url) {
        throw new Error("Chat link not available");
      }
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      }
      openWhatsAppShareLink(url);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to generate chat link",
      );
    } finally {
      setMagicLinkLoading(false);
    }
  };

  const handleSendText = async () => {
    if (!newMessage.trim()) return;
    const text = newMessage.trim();
    setNewMessage("");
    await sendMessage("text", { text });
  };

  const handleSendRatingRequest = async () => {
    if (!selectedConversation?.id) return;
    const requestId =
      window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random()}`;
    await sendMessage("rating_request", {
      requestId,
      prompt: "Please rate your experience.",
      maxRating: 5,
    });
  };

  const handleSendRatingResponse = async (requestId, rating) => {
    if (!requestId || !rating) return;
    await sendMessage("rating_response", { requestId, rating });
  };

  const handleMessageKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendText();
    }
  };

  const openMessageMenu = (event, message) => {
    event.preventDefault();
    setMessageMenuAnchor(event.currentTarget);
    setMessageMenuMessage(message);
  };

  const closeMessageMenu = () => {
    setMessageMenuAnchor(null);
    setMessageMenuMessage(null);
  };

  const openDeleteDialog = () => {
    setDeleteDialogMessage(messageMenuMessage);
    setDeleteDialogOpen(true);
    closeMessageMenu();
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeleteDialogMessage(null);
  };

  const canDeleteForEveryone = (message) =>
    message?.sender_id === user?.id || operatorRoles.includes(user?.role);

  const handleDeleteMessage = async (scope) => {
    const message = deleteDialogMessage;
    if (!message || !selectedConversation?.id) return;

    try {
      await axios.delete(
        `${apiUrl}/confirmations/conversations/${selectedConversation.id}/messages/${message.id}`,
        {
          headers: authHeaders,
          params: { scope },
        },
      );

      if (scope === "everyone") {
        setMessages((prev) =>
          prev.map((item) =>
            item.id === message.id
              ? {
                  ...item,
                  deleted_for_everyone: true,
                  content: { text: "This message was deleted." },
                }
              : item,
          ),
        );
      } else {
        setMessages((prev) => prev.filter((item) => item.id !== message.id));
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete message");
    } finally {
      closeMessageMenu();
      closeDeleteDialog();
    }
  };

  const handleDecision = async (decision) => {
    if (!selectedConversation?.application_id) return;
    try {
      await axios.post(
        `${apiUrl}/confirmations/applications/${selectedConversation.application_id}/decision`,
        { decision },
        { headers: authHeaders },
      );
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update confirmation");
    }
  };

  const handleFinalSubmit = async () => {
    if (!selectedConversation?.application_id) return;
    try {
      await axios.post(
        `${apiUrl}/confirmations/applications/${selectedConversation.application_id}/final-submit`,
        {},
        { headers: authHeaders },
      );
    } catch (err) {
      setError(err.response?.data?.message || "Final submit failed");
    }
  };

  const createConversation = async (payload) => {
    const response = await axios.post(
      `${apiUrl}/confirmations/conversations`,
      payload,
      { headers: authHeaders },
    );
    return response.data?.data;
  };

  const handleStartChatFromBill = async (bill) => {
    const phone = bill?.customerPhone || bill?.customer_phone;
    if (!phone) {
      setError("Customer phone number is required to start chat");
      return;
    }

    try {
      const created = await createConversation({ userPhone: phone });
      if (!created?.id) return;
      setConversations((prev) => {
        const rest = prev.filter((conv) => conv.id !== created.id);
        return [created, ...rest];
      });
      await fetchMessages(created.id);
      scrollToChatPanel();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to start chat");
    }
  };

  const ratingResponsesByRequestId = useMemo(() => {
    const map = new Map();
    messages.forEach((message) => {
      if (message.message_type === "rating_response") {
        const requestId = message.content?.requestId;
        if (requestId) {
          map.set(requestId, message);
        }
      }
    });
    return map;
  }, [messages]);

  const conversationsPanel = (
    <Card
      sx={{
        height: "100%",
        borderRadius: 3,
        border: "1px solid rgba(148,163,184,0.2)",
        boxShadow: "0 12px 30px rgba(15,23,42,0.08)",
      }}
    >
      <CardContent
        sx={{ display: "flex", flexDirection: "column", height: "100%" }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            Conversations
          </Typography>
          <Button
            size="small"
            onClick={fetchConversations}
            disabled={loadingConversations}
          >
            Refresh
          </Button>
        </Stack>
        <Stack spacing={1.2} sx={{ flex: 1, overflowY: "auto", pr: 0.5 }}>
          {loadingConversations && (
            <Typography variant="body2">Loading...</Typography>
          )}
          {!loadingConversations && conversations.length === 0 && (
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                No conversations yet. Ask the operator to start a chat using
                your phone number.
              </Typography>
              {!isOperator && user?.phone && (
                <Typography variant="caption" color="text.secondary">
                  Your phone: {user.phone}
                </Typography>
              )}
            </Stack>
          )}
          {conversations.map((conversation) => (
            <Card
              key={conversation.id}
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 2,
                borderColor:
                  selectedConversation?.id === conversation.id
                    ? "primary.main"
                    : "transparent",
                bgcolor:
                  selectedConversation?.id === conversation.id
                    ? "rgba(25,118,210,0.08)"
                    : "#ffffff",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "primary.light",
                  boxShadow: "0 10px 24px rgba(15,23,42,0.08)",
                  transform: "translateY(-1px)",
                },
              }}
              onClick={() => handleSelectConversation(conversation.id)}
            >
              <Stack spacing={0.6}>
                <Typography fontWeight={600}>
                  {conversation.user_name || "User"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {conversation.job_alert_name || "Application"} |{" "}
                  {conversation.application_number || "No number"}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    size="small"
                    label={conversation.confirmation_status || "no preview"}
                    color={
                      conversation.confirmation_status === "confirmed"
                        ? "success"
                        : conversation.confirmation_status === "rejected"
                          ? "error"
                          : conversation.confirmation_status === "pending"
                            ? "warning"
                            : "default"
                    }
                  />
                  <Typography variant="caption" color="text.secondary">
                    {conversation.last_message_created_at
                      ? new Date(
                          conversation.last_message_created_at,
                        ).toLocaleString()
                      : ""}
                  </Typography>
                </Stack>
              </Stack>
            </Card>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );

  const handleCreateConversation = async () => {
    setNewConversationError("");
    const payload = {};
    if (newConversationAppId.trim()) {
      payload.applicationId = newConversationAppId.trim();
    }
    if (!payload.applicationId && newConversationPhone.trim()) {
      payload.userPhone = newConversationPhone.trim();
    }

    if (!payload.applicationId && !payload.userPhone) {
      setNewConversationError("Enter application ID or user phone number.");
      return;
    }

    try {
      const response = await axios.post(
        `${apiUrl}/confirmations/conversations`,
        payload,
        { headers: authHeaders },
      );
      const created = response.data.data;
      setConversations((prev) => [created, ...prev]);
      setNewConversationOpen(false);
      setNewConversationAppId("");
      setNewConversationPhone("");
      await fetchMessages(created.id);
    } catch (err) {
      setNewConversationError(
        err.response?.data?.message || "Failed to create conversation",
      );
    }
  };

  const renderMessage = (message) => {
    const isOwn =
      (message.sender_type === "operator" && isOperator) ||
      (message.sender_type === "user" && !isOperator);
    const isDeleted = Boolean(message.deleted_for_everyone);
    const bubbleColor =
      message.sender_type === "system"
        ? theme.palette.grey[200]
        : isOwn
          ? "#1f2937"
          : "#0f766e";

    const textColor =
      message.sender_type === "system" ? theme.palette.text.primary : "#ffffff";

    if (isDeleted) {
      return (
        <Box
          key={message.id}
          sx={{
            alignSelf: "center",
            maxWidth: "80%",
            px: 2,
            py: 1,
            borderRadius: 2,
            bgcolor: theme.palette.grey[200],
            color: theme.palette.text.secondary,
            fontStyle: "italic",
          }}
        >
          <Typography variant="body2">This message was deleted.</Typography>
        </Box>
      );
    }

    const renderMessageActions = () => {
      if (message.sender_type === "system" || message.deleted_for_everyone)
        return null;
      return (
        <IconButton
          size="small"
          onClick={(event) => openMessageMenu(event, message)}
          sx={{
            position: "absolute",
            top: 6,
            right: 6,
            zIndex: 2,
            color: "#f8fafc",
            bgcolor: "rgba(15,23,42,0.45)",
            border: "1px solid rgba(148,163,184,0.45)",
            "&:hover": {
              bgcolor: "rgba(15,23,42,0.7)",
              color: "#ffffff",
            },
          }}
          title="Options"
        >
          <KeyboardArrowDownIcon fontSize="inherit" />
        </IconButton>
      );
    };

    if (message.message_type === "application_preview") {
      const preview = message.content || {};
      return (
        <Card
          key={message.id}
          sx={{
            p: 2,
            pr: 6,
            bgcolor: "#111827",
            color: "#ffffff",
            borderRadius: 2,
            maxWidth: 520,
            position: "relative",
          }}
        >
          {renderMessageActions()}
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <PreviewIcon fontSize="small" />
              <Typography fontWeight={700}>
                {preview.title || "Application Preview"}
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {preview.summary}
            </Typography>
            <Divider sx={{ borderColor: "rgba(255,255,255,0.2)" }} />
            <Stack spacing={1}>
              {(preview.fields || []).map((field, index) => (
                <Stack
                  key={`${field.label}-${index}`}
                  direction="row"
                  justifyContent="space-between"
                >
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    {field.label}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {field.value}
                  </Typography>
                </Stack>
              ))}
            </Stack>
            {preview.pdfUrl && (
              <Button
                variant="outlined"
                size="small"
                endIcon={<LaunchIcon />}
                onClick={() => window.open(preview.pdfUrl, "_blank")}
                sx={{ borderColor: "#38bdf8", color: "#38bdf8" }}
              >
                View PDF
              </Button>
            )}
          </Stack>
        </Card>
      );
    }

    if (message.message_type === "image") {
      const image = message.content || {};
      const imageUrl = resolveMediaUrl(image.url || "");

      return (
        <Box
          key={message.id}
          sx={{
            alignSelf:
              message.sender_type === "system"
                ? "center"
                : isOwn
                  ? "flex-end"
                  : "flex-start",
            maxWidth: message.sender_type === "system" ? "80%" : "70%",
          }}
        >
          <Card
            sx={{
              p: 1.5,
              pr: 6,
              bgcolor: "#111827",
              color: "#ffffff",
              borderRadius: 2,
              position: "relative",
            }}
          >
            {renderMessageActions()}
            <Stack spacing={1}>
              <Box
                component="img"
                src={imageUrl}
                alt={image.name || "Chat image"}
                sx={{
                  width: "100%",
                  maxHeight: 360,
                  objectFit: "contain",
                  borderRadius: 1.5,
                  bgcolor: "#0b1220",
                  cursor: imageUrl ? "pointer" : "default",
                }}
                onClick={() => imageUrl && window.open(imageUrl, "_blank")}
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => openWhatsAppShare(imageUrl)}
                >
                  WhatsApp
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => openTelegramShare(imageUrl)}
                >
                  Telegram
                </Button>
              </Stack>
            </Stack>
          </Card>
        </Box>
      );
    }

    if (message.message_type === "document") {
      const doc = message.content || {};
      const docUrl = resolveMediaUrl(doc.url || "");
      return (
        <Box
          key={message.id}
          sx={{
            alignSelf:
              message.sender_type === "system"
                ? "center"
                : isOwn
                  ? "flex-end"
                  : "flex-start",
            maxWidth: message.sender_type === "system" ? "80%" : "70%",
          }}
        >
          <Card
            sx={{
              p: 1.5,
              pr: 6,
              bgcolor: "#111827",
              color: "#ffffff",
              borderRadius: 2,
              position: "relative",
            }}
          >
            {renderMessageActions()}
            <Stack spacing={1} direction="row" alignItems="center">
              <DescriptionIcon fontSize="small" />
              <Stack spacing={0}>
                <Typography variant="body2" fontWeight={600}>
                  {doc.name || "Document"}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  {formatFileSize(doc.size)}
                  {doc.mime ? ` • ${doc.mime}` : ""}
                </Typography>
              </Stack>
              <Button
                size="small"
                variant="outlined"
                sx={{ ml: "auto" }}
                onClick={() => docUrl && window.open(docUrl, "_blank")}
              >
                Open
              </Button>
            </Stack>
          </Card>
        </Box>
      );
    }

    if (message.message_type === "rating_request") {
      const requestId = message.content?.requestId || message.id;
      const responseMessage = ratingResponsesByRequestId.get(requestId);
      const responseRating = responseMessage?.content?.rating || 0;
      const maxRating = message.content?.maxRating || 5;
      const prompt = message.content?.prompt || "Please rate your experience.";

      return (
        <Box
          key={message.id}
          sx={{
            alignSelf:
              message.sender_type === "system"
                ? "center"
                : isOwn
                  ? "flex-end"
                  : "flex-start",
            maxWidth: message.sender_type === "system" ? "80%" : "70%",
          }}
        >
          <Card
            sx={{
              p: 2,
              pr: 6,
              bgcolor: "#111827",
              color: "#ffffff",
              borderRadius: 2,
              position: "relative",
            }}
          >
            {renderMessageActions()}
            <Stack spacing={1}>
              <Typography variant="body2" fontWeight={600}>
                {prompt}
              </Typography>
              {!isOperator ? (
                <Stack direction="row" spacing={2} alignItems="center">
                  <Rating
                    value={responseRating}
                    max={maxRating}
                    onChange={(_, value) =>
                      handleSendRatingResponse(requestId, value)
                    }
                    disabled={Boolean(responseRating)}
                  />
                  {responseRating ? (
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      Thanks! You rated {responseRating}/{maxRating}.
                    </Typography>
                  ) : null}
                </Stack>
              ) : (
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  {responseRating
                    ? `User rated ${responseRating}/${maxRating}.`
                    : "Waiting for user rating."}
                </Typography>
              )}
            </Stack>
          </Card>
        </Box>
      );
    }

    if (message.message_type === "rating_response") {
      const rating = message.content?.rating || 0;
      return (
        <Box
          key={message.id}
          sx={{
            alignSelf:
              message.sender_type === "system"
                ? "center"
                : isOwn
                  ? "flex-end"
                  : "flex-start",
            maxWidth: message.sender_type === "system" ? "80%" : "70%",
          }}
        >
          <Card
            sx={{
              p: 1.5,
              pr: 6,
              bgcolor: "#111827",
              color: "#ffffff",
              borderRadius: 2,
              position: "relative",
            }}
          >
            {renderMessageActions()}
            <Stack spacing={1}>
              <Typography variant="body2">Rating received</Typography>
              <Rating value={rating} readOnly />
            </Stack>
          </Card>
        </Box>
      );
    }

    return (
      <Box
        key={message.id}
        sx={{
          alignSelf:
            message.sender_type === "system"
              ? "center"
              : isOwn
                ? "flex-end"
                : "flex-start",
          maxWidth: message.sender_type === "system" ? "80%" : "70%",
          px: 2,
          py: 1,
          pr: 6,
          borderRadius: 2,
          bgcolor: bubbleColor,
          color: textColor,
          position: "relative",
        }}
      >
        {renderMessageActions()}
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
          {message.content?.text || ""}
        </Typography>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        px: { xs: 1.5, sm: 2, md: 4 },
        py: { xs: 2, md: 3 },
        background:
          "radial-gradient(circle at top, #e0f2fe 0%, #f8fafc 42%, #eef2ff 100%)",
      }}
    >
      <Card
        sx={{
          mb: 2,
          borderRadius: 3,
          border: "1px solid rgba(148,163,184,0.2)",
          boxShadow: "0 16px 40px rgba(15,23,42,0.08)",
        }}
      >
        <CardContent>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ sm: "center" }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "rgba(59,130,246,0.15)",
                }}
              >
                <ChatIcon color="primary" />
              </Box>
              <Stack spacing={0}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="h5" fontWeight={700}>
                    Application Chat
                  </Typography>
                  <Chip
                    size="small"
                    label={
                      socketConnected
                        ? "Live"
                        : socketDisabled
                          ? "HTTP Mode"
                          : "Connecting..."
                    }
                    color={
                      socketConnected
                        ? "success"
                        : socketDisabled
                          ? "warning"
                          : "default"
                    }
                    sx={{
                      height: 20,
                      fontSize: "0.7rem",
                      "& .MuiChip-label": { px: 1 },
                    }}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {socketConnected
                    ? "Real-time messaging active"
                    : socketDisabled
                      ? "Using HTTP polling (checks every 30 seconds)"
                      : "Attempting to connect..."}
                </Typography>
              </Stack>
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ ml: { sm: "auto" } }}
            >
              {isMobile && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setMobileListOpen(true)}
                >
                  Conversations
                </Button>
              )}
              {isOperator && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => setNewConversationOpen(true)}
                >
                  New Conversation
                </Button>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {socketDisabled && isOperator && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Socket.IO connection failed.</strong> Using HTTP polling
          fallback mode (checks every 30 seconds). This is usually due to CORS
          configuration. Please ensure backend changes are deployed correctly.
        </Alert>
      )}

      {isOperator && (
        <Box sx={{ mb: 3 }}>
          <PendingWorks onStartChat={handleStartChatFromBill} showChatAction />
        </Box>
      )}

      <Grid container spacing={2} alignItems="stretch">
        {!isMobile && (
          <Grid item xs={12} md={4}>
            {conversationsPanel}
          </Grid>
        )}

        <Grid item xs={12} md={8} ref={chatPanelRef}>
          <Card
            sx={{
              height: { xs: "calc(100vh - 320px)", md: "calc(100vh - 260px)" },
              minHeight: { xs: 520, md: 640 },
              borderRadius: 3,
              border: "1px solid rgba(148,163,184,0.2)",
              boxShadow: "0 16px 40px rgba(15,23,42,0.08)",
            }}
          >
            <CardContent
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                p: { xs: 2, md: 3 },
              }}
            >
              {!selectedConversation ? (
                <Box sx={{ p: 4, textAlign: "center" }}>
                  <Typography variant="body1" color="text.secondary">
                    Select a conversation to start chatting.
                  </Typography>
                </Box>
              ) : (
                <>
                  <Stack spacing={1.5} sx={{ mb: 2 }}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ sm: "center" }}
                    >
                      <Typography variant="h6" fontWeight={700}>
                        {selectedConversation.user_name || "User"}
                      </Typography>
                      {isOperator && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<LinkIcon />}
                          onClick={handleShareChatLink}
                          disabled={magicLinkLoading}
                          sx={{ ml: { sm: "auto" } }}
                        >
                          Share Chat Link
                        </Button>
                      )}
                    </Stack>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ sm: "center" }}
                    >
                      <Chip
                        size="small"
                        label={`Status: ${selectedConversation.confirmation_status || "no preview"}`}
                      />
                      {selectedConversation.confirmed_at && (
                        <Typography variant="caption" color="text.secondary">
                          Confirmed at{" "}
                          {new Date(
                            selectedConversation.confirmed_at,
                          ).toLocaleString()}
                        </Typography>
                      )}
                    </Stack>
                    {isOperator && (
                      <Stepper activeStep={activeStep} alternativeLabel>
                        {steps.map((label) => (
                          <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                          </Step>
                        ))}
                      </Stepper>
                    )}
                  </Stack>

                  <Divider sx={{ mb: 2 }} />

                  <Box
                    ref={scrollRef}
                    sx={{
                      flex: 1,
                      overflowY: "auto",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                      px: { xs: 0.5, sm: 1.5 },
                      py: 1,
                      borderRadius: 2,
                      bgcolor: "rgba(248,250,252,0.7)",
                      scrollbarGutter: "stable",
                    }}
                    onPaste={handlePasteImage}
                  >
                    {loadingMessages && (
                      <Typography variant="body2">
                        Loading messages...
                      </Typography>
                    )}
                    {!loadingMessages && messages.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        No messages yet. Start the conversation.
                      </Typography>
                    )}
                    {messages.map(renderMessage)}
                  </Box>

                  <Divider sx={{ mt: 2, mb: 2 }} />

                  {isOperator ? (
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems="center"
                    >
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Write a message..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onPaste={handlePasteImage}
                        onKeyDown={handleMessageKeyDown}
                        inputProps={{ onPaste: handlePasteImage }}
                        multiline
                        minRows={1}
                        maxRows={4}
                      />
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        onChange={handleFileChange}
                        style={{ display: "none" }}
                      />
                      <input
                        ref={documentInputRef}
                        type="file"
                        accept="application/pdf"
                        onChange={handleDocumentChange}
                        style={{ display: "none" }}
                      />
                      <IconButton
                        color="primary"
                        disabled={uploadingImage}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <ImageIcon />
                      </IconButton>
                      <IconButton
                        color="primary"
                        disabled={uploadingDocument}
                        onClick={() => documentInputRef.current?.click()}
                      >
                        <DescriptionIcon />
                      </IconButton>
                      <Button
                        variant="contained"
                        endIcon={<SendIcon />}
                        onClick={handleSendText}
                        disabled={uploadingImage || uploadingDocument}
                      >
                        Send
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<StarOutlineIcon />}
                        onClick={handleSendRatingRequest}
                      >
                        Request Rating
                      </Button>
                      <Button
                        variant="contained"
                        color="success"
                        disabled={
                          selectedConversation.confirmation_status !==
                          "confirmed"
                        }
                        onClick={handleFinalSubmit}
                      >
                        Final Submit
                      </Button>
                    </Stack>
                  ) : (
                    <Stack spacing={1}>
                      {selectedConversation.confirmation_status ===
                        "pending" && (
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                        >
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={<ConfirmIcon />}
                            onClick={() => handleDecision("YES")}
                          >
                            YES - Confirm
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<RejectIcon />}
                            onClick={() => handleDecision("NO")}
                          >
                            NO - Edit Required
                          </Button>
                        </Stack>
                      )}
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        alignItems="center"
                      >
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Write a message or correction..."
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onPaste={handlePasteImage}
                          onKeyDown={handleMessageKeyDown}
                          inputProps={{ onPaste: handlePasteImage }}
                          multiline
                          minRows={1}
                          maxRows={4}
                        />
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/webp"
                          onChange={handleFileChange}
                          style={{ display: "none" }}
                        />
                        <input
                          ref={documentInputRef}
                          type="file"
                          accept="application/pdf"
                          onChange={handleDocumentChange}
                          style={{ display: "none" }}
                        />
                        <IconButton
                          color="primary"
                          disabled={uploadingImage}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <ImageIcon />
                        </IconButton>
                        <IconButton
                          color="primary"
                          disabled={uploadingDocument}
                          onClick={() => documentInputRef.current?.click()}
                        >
                          <DescriptionIcon />
                        </IconButton>
                        <Button
                          variant="contained"
                          endIcon={<SendIcon />}
                          onClick={handleSendText}
                          disabled={uploadingImage || uploadingDocument}
                        >
                          Send
                        </Button>
                      </Stack>
                    </Stack>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={newConversationOpen}
        onClose={() => setNewConversationOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Start New Conversation</DialogTitle>
        <DialogContent>
          {newConversationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {newConversationError}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Application ID"
              value={newConversationAppId}
              onChange={(e) => setNewConversationAppId(e.target.value)}
              fullWidth
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textAlign: "center" }}
            >
              OR
            </Typography>
            <TextField
              label="User Phone"
              value={newConversationPhone}
              onChange={(e) => setNewConversationPhone(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewConversationOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateConversation}>
            Start
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={messageMenuAnchor}
        open={Boolean(messageMenuAnchor)}
        onClose={closeMessageMenu}
      >
        <MenuItem onClick={openDeleteDialog}>
          <Stack direction="row" spacing={1} alignItems="center">
            <DeleteOutlineIcon fontSize="small" />
            <Typography variant="body2">Delete</Typography>
          </Stack>
        </MenuItem>
      </Menu>

      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete message?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Choose how you want to delete this message.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: "space-between" }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ width: "100%" }}
          >
            {canDeleteForEveryone(deleteDialogMessage) && (
              <Button
                variant="contained"
                color="success"
                onClick={() => handleDeleteMessage("everyone")}
              >
                Delete for everyone
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={() => handleDeleteMessage("me")}
            >
              Delete for me
            </Button>
            <Button
              variant="text"
              onClick={closeDeleteDialog}
              sx={{ ml: "auto" }}
            >
              Cancel
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="left"
        open={mobileListOpen}
        onClose={() => setMobileListOpen(false)}
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: 360,
            p: 2,
            bgcolor: "#f8fafc",
          },
        }}
      >
        <Stack spacing={2} sx={{ height: "100%" }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" fontWeight={700}>
              Conversations
            </Typography>
            <Button size="small" onClick={() => setMobileListOpen(false)}>
              Close
            </Button>
          </Stack>
          {conversationsPanel}
        </Stack>
      </Drawer>
    </Box>
  );
};

export default ApplicationChat;
